import './SegmentedTable.less';
import React from 'react';
import {getDocumentOffsetPosition} from '../../utils/dom';
import {setClickOutsideToClose} from "../../dom/event-handlers/click-outside-to-close";
import {getLayoutScrollElement} from "../../dom/element/queries";
import Icon from "../icon/Icon";
import {jsonCopy} from "../../utils/json";

const SCROLL_BAR_SCALE_RATIO = 1;

export default class SegmentedTable extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            inlineEditing: {
                groupId: null,
                itemId: null,
                column: null
            },
            selectedItemIds: props.selectedItemIds ? JSON.parse(JSON.stringify(props.selectedItemIds)) : [],
            horizontalScrollDisabledTemporarily: false,
            shouldShowInlineEditor: false
        };

        /** @type {HTMLDivElement} */
        this.stickyHeader = null;

        /** @type {HTMLTableRowElement} */
        this.stickyHeaderRow = null;

        /** @type {HTMLTableElement} */
        this.table = null;

        /** @type {HTMLTableRowElement} */
        this.tableHeaderRow = null;

        /** @type {HTMLDivElement} */
        this.inlineEditor = null;

        /** @type {HTMLTableCellElement} */
        this.editingCell = null;

        /** @type {HTMLButtonElement} */
        this.editingCellEditButton = null;

        /** @type {HTMLDivElement} */
        this.scrollView = null;

        /** @type {HTMLDivElement} */
        this.scrollBar = null;

        /** @type {HTMLDivElement} */
        this.scrollBarTrack = null;

        /** @type {HTMLDivElement} */
        this.scrollBarThumb = null;
    }

    componentDidMount() {
        this.initOverlayScroll();
        window.addEventListener('resize', () => {
            this.updateStickyHeader();
            this.updateOverlayScroll();
            this.updateInlineEditorPosition();
        });
        window.addEventListener('scroll', () => {
            this.updateInlineEditorPosition();
        });
        this.scrollView.addEventListener('scroll', () => {
            this.updateInlineEditorPosition();
        });
        const layoutScrollElement = getLayoutScrollElement();
        if (layoutScrollElement) {
            layoutScrollElement.addEventListener('scroll', () => {
                this.updateInlineEditorPosition();
            });
        }
    }

    componentDidUpdate() {
        this.removeSelectedItemIdsNoLongerExisted();
        this.updateStickyHeader();
        this.updateOverlayScroll();
        this.setScrollViewOverflow();
        this.clearEditingRefsIfNeeded();
        this.updateShouldShowInlineEditor();
        this.setClickOutsideInlineEditor();

        requestAnimationFrame(() => {
            this.updateInlineEditorPosition();
        });
    }

    updateStickyHeader = () => {
        if (this.stickyHeaderRow === null || this.tableHeaderRow === null) {
            return;
        }

        [].forEach.call(this.stickyHeaderRow.children, (stickyCell, index) => {
            stickyCell.style.width =
                stickyCell.style.minWidth =
                    stickyCell.style.maxWidth =
                        this.tableHeaderRow.children.item(index).offsetWidth + 'px';
        });

        this.stickyHeader.style.marginBottom = -this.tableHeaderRow.offsetHeight + 'px';

        this.scrollView.addEventListener('scroll', () => {
            this.stickyHeader.scrollLeft = this.scrollView.scrollLeft;
        });
    };

    updateShouldShowInlineEditor = () => {
        if (this.editingCellEditButton && !this.state.shouldShowInlineEditor) {
            this.setState({
                shouldShowInlineEditor: true
            });
        } else if (!this.editingCellEditButton && this.state.shouldShowInlineEditor) {
            this.setState({
                shouldShowInlineEditor: false
            });
        }
    };

    setScrollViewOverflow = () => {
        const {horizontalScrollDisabledTemporarily} = this.state;

        // check if scroll view has error with overflow
        // caused by table height change dramatically
        // then set overflow by hidden to fix

        // If we check scrollHeight === clientHeight
        // it may not work when users zoom in or out
        // browser will round number of pixels
        // adding 1px to make sure it work when zooming
        if (this.scrollView.scrollHeight > this.scrollView.clientHeight + 1) {
            if (!horizontalScrollDisabledTemporarily) {
                this.setState({
                    horizontalScrollDisabledTemporarily: true
                });
            }
            return;
        }

        if (horizontalScrollDisabledTemporarily) {
            setTimeout(() => {
                this.setState({
                    horizontalScrollDisabledTemporarily: false
                });
            }, 0);
        }
    };

    removeSelectedItemIdsNoLongerExisted = () => {
        const {items, updateSelectedItemIds} = this.props;
        const {selectedItemIds} = this.state;
        const newSelectedItemIds = [];

        selectedItemIds.forEach(id => {
            if (items.find(item => item.id === id)) {
                newSelectedItemIds.push(id);
            }
        });

        if (newSelectedItemIds.length !== selectedItemIds.length) {
            this.setState({
                selectedItemIds: newSelectedItemIds
            });
            updateSelectedItemIds(newSelectedItemIds);
        }
    };

    setClickOutsideInlineEditor = () => {
        if (this.editingCellEditButton && this.inlineEditor) {
            setClickOutsideToClose(
                'components.SegmentedTable.inlineEditor',
                [this.editingCellEditButton, this.inlineEditor],
                () => {
                    if (!this.state.inlineEditorIsSubmitting) {
                        this.setState({
                            inlineEditing: {
                                groupId: null,
                                itemId: null,
                                column: null
                            }
                        });
                    }
                }
            );
        }
    };

    closeInlineEditor = () => {
        if (!this.state.inlineEditorIsSubmitting) {
            this.setState({
                inlineEditing: {
                    groupId: null,
                    itemId: null,
                    column: null
                }
            });
        }
    };

    clearEditingRefsIfNeeded = () => {
        const {columnConfigs, items} = this.props;

        const {inlineEditing} = this.state;

        const editingItem = items.find(item => inlineEditing.itemId === item.id);

        const editingColumnConfig = columnConfigs.find(columnConfig => inlineEditing.column === columnConfig.key);

        if (!editingItem || !editingColumnConfig) {
            this.editingCell = null;
            this.inlineEditor = null;
            this.editingCellEditButton = null;
        }
    };

    updateOverlayScroll = () => {
        if (this.scrollView === null) {
          return;
        }
        const scrollView = this.scrollView;
        const scrollBar = this.scrollBar;
        const scrollBarTrack = this.scrollBarTrack;
        const scrollBarThumb = this.scrollBarThumb;
        const scaleRatio = SCROLL_BAR_SCALE_RATIO;
        const clientWidth = scrollView.clientWidth;
        const scrollWidth = scrollView.scrollWidth;
        const scrollViewRect = scrollView.getBoundingClientRect();
        scrollBar.style.width = scaleRatio * clientWidth + 'px';
        scrollBar.style.left = ((clientWidth - scaleRatio * clientWidth) + scrollViewRect.left) + 'px';
        scrollBarTrack.style.width = scaleRatio * clientWidth + 'px';
        scrollBarThumb.style.width = scaleRatio * clientWidth * (clientWidth / scrollWidth) + 'px';
        if (scrollWidth > clientWidth
            && scrollViewRect.top < window.innerHeight
            && scrollViewRect.bottom > window.innerHeight
        ) {
            scrollBar.style.display = 'block';
        } else {
            scrollBar.style.display = 'none';
        }
    };

    initOverlayScroll = () => {
        const scrollView = this.scrollView;
        const scrollBarThumb = this.scrollBarThumb;
        const scaleRatio = SCROLL_BAR_SCALE_RATIO;
        scrollView.addEventListener('scroll', () => {
            const clientWidth = scrollView.clientWidth;
            const scrollWidth = scrollView.scrollWidth;
            const scrollLeft = scrollView.scrollLeft;
            scrollBarThumb.style.left = scaleRatio * scrollLeft * (clientWidth / scrollWidth) + 'px';
        });
        let pointerStartLeft = null;
        let scrollStartLeft = null;

        const startScroll = (ev) => {
            pointerStartLeft = ev.clientX;
            scrollStartLeft = scrollView.scrollLeft;
            scrollBarThumb.classList.add('holding');
        };
        const endScroll = () => {
            pointerStartLeft = null;
            scrollStartLeft = null;
            scrollBarThumb.classList.remove('holding');
        };
        const onScroll = (ev) => {
            const delta = ev.clientX - pointerStartLeft;
            const clientWidth = scrollView.clientWidth;
            const scrollWidth = scrollView.scrollWidth;
            const scrollVelocity = scrollWidth / clientWidth;
            scrollView.scrollLeft = scrollStartLeft + scrollVelocity * delta / scaleRatio;
        };

        document.addEventListener('mousedown', (ev) => {
            if (ev.target !== scrollBarThumb && !scrollBarThumb.contains(ev.target)) {
                return;
            }
            ev.preventDefault();
            startScroll(ev);
        });
        document.addEventListener('mouseup', (ev) => {
            if (pointerStartLeft === null || scrollStartLeft === null) {
                return;
            }
            ev.preventDefault();
            endScroll();
        });
        document.addEventListener('mousemove', (ev) => {
            if (pointerStartLeft === null || scrollStartLeft === null) {
                return;
            }
            ev.preventDefault();
            onScroll(ev);
        });

        const layoutScrollElement = getLayoutScrollElement();
        if (layoutScrollElement) {
            layoutScrollElement.addEventListener('scroll', () => {
                this.updateOverlayScroll();
            });
        } else {
            window.addEventListener('scroll', () => {
                this.updateOverlayScroll();
            });
        }
    };

    updateInlineEditorPosition = () => {
        const editingCell = this.editingCell;
        const inlineEditor = this.inlineEditor;

        if (!editingCell || !inlineEditor) {
            return;
        }

        const scrollView = this.scrollView;

        // editingCell.offsetTop and editingCell.offsetLeft work on Chrome and Firefox differently
        // so we will use dom offset position instead

        const editingCellDOP = getDocumentOffsetPosition(editingCell);
        const scrollViewDOP = getDocumentOffsetPosition(scrollView);
        const scrollViewRect = scrollView.getBoundingClientRect();

        const dropMargin = 1;
        const edgeMargin = 10;

        inlineEditor.style.top = Math.max(edgeMargin, Math.min(
            editingCellDOP.top - scrollViewDOP.top
            + editingCell.offsetHeight
            - scrollView.scrollTop
            + dropMargin,
            scrollView.offsetHeight - inlineEditor.offsetHeight
            + window.innerHeight - scrollViewRect.bottom
            - edgeMargin
        )) + 'px';

        inlineEditor.style.left = Math.max(0, Math.min(
            editingCellDOP.left - scrollViewDOP.left - scrollView.scrollLeft,
            scrollView.offsetWidth - inlineEditor.offsetWidth
        )) + 'px';
    };

    toggleItemSelected = (id) => {
        const { updateSelectedItemIds } = this.props;
        const { selectedItemIds } = this.state;
        const index = selectedItemIds.indexOf(id);

        if (index > -1) {
            selectedItemIds.splice(index, 1);
        } else {
            selectedItemIds.push(id);
        }

        this.setState({
            selectedItemIds
        }, () => {
            updateSelectedItemIds && updateSelectedItemIds(selectedItemIds);
        });
    };

    toggleAllItemsSelected = () => {
        const { items, updateSelectedItemIds } = this.props;
        let { selectedItemIds } = this.state;

        if (selectedItemIds.length === items.length) {
            selectedItemIds = [];
        } else {
            selectedItemIds = items.map(item => item.id);
        }

        this.setState({
            selectedItemIds
        }, () => {
            updateSelectedItemIds && updateSelectedItemIds(selectedItemIds);
        });
    };

    /**
     *
     * @param itemId
     * @param rowIndex
     * @param groupId
     * @return {string}
     */
    getItemRowKey = (itemId, rowIndex, groupId) => {
        return JSON.stringify({itemId, rowIndex, groupId});
    };

    /**
     *
     * @param groupId
     * @param rowIndex
     * @return {string}
     */
    getGroupRowKey = (groupId, rowIndex) => {
        return JSON.stringify({groupId, rowIndex});
    };

    /**
     *
     * @param itemId
     * @param groupId
     * @return {ReactComponent[]}
     */
    getSegmentedItemRows = (itemId, groupId = undefined) => {
        const {
            columnConfigs,
            items,
            segmentData,
            segmentNames,
            segmentNameColumn,
            segmentImmutableColumns,
            selectable
        } = this.props;

        const { inlineEditing, inlineEditorIsSubmitting, selectedItemIds } = this.state;

        let item = null, itemIndex = null;
        items.forEach((item1, index) => {
            if (item1.id === itemId) {
                item = item1;
                itemIndex = index;
            }
        });
        item.__type = 'item';

        const rowObjects = [item];
        if (segmentData && segmentData.length > 0) {
            segmentData[itemIndex].forEach((seg, segIndex) => {
                if (seg === null) {
                    return;
                }
                const obj = {__type: 'segment', id: item.id};
                let isSegmentStarted = false;
                columnConfigs.forEach(({key}) => {
                    if (key === segmentNameColumn) {
                        obj[key] = segmentNames ? segmentNames[segIndex] : seg[key];
                        isSegmentStarted = true;
                        return;
                    }

                    if (!isSegmentStarted) {
                        return;
                    }

                    if (segmentImmutableColumns.indexOf(key) > -1) {
                        obj[key] = {__type: 'segmentImmutable'};
                        return;
                    }

                    if (!seg.hasOwnProperty(key)) {
                        return;
                    }

                    obj[key] = seg[key];
                });
                rowObjects.push(obj);
            });
        }
        rowObjects.push({__type: 'divider', id: item.id});

        return rowObjects.map((item, index) => {
            const selected = selectedItemIds.indexOf(item.id) >= 0;
            const editing = inlineEditing.itemId === item.id;
            return <tr key={this.getItemRowKey(item.id, index, groupId)}
                       className={
                           `row-${item.__type}`
                           + (editing ? ' editing' : '')
                           + (selected ? ' selected' : '')
                       }
            >
                {
                    selectable &&
                    <td className="cell-checkbox">
                        {
                            item.__type === 'item' &&
                            <div className="checkbox"
                                 onClick={() => this.toggleItemSelected(item.id)}
                            >
                                {
                                    selected
                                        ? <Icon name="checkbox-checked"/>
                                        : <Icon name="checkbox-blank"/>
                                }
                            </div>
                        }
                    </td>
                }
                {
                    columnConfigs.map(({key, renderValue = val => val, renderEditor, halign = 'left', rightAlign}) => {
                        const cellEmpty = item.__type === 'divider' || (!item.hasOwnProperty(key) && !key.startsWith('*'));
                        const isSegmentImmutable = item[key] !== null && item[key] !== undefined && item[key].__type === 'segmentImmutable';
                        const editable = item.__type === 'item' && !!renderEditor && this.getEditor(item, renderEditor);
                        const editing = item.__type === 'item'
                            && inlineEditing.itemId === item.id
                            && inlineEditing.column === key
                            && inlineEditing.groupId === groupId;
                        const renderedValue = !cellEmpty && !isSegmentImmutable ? renderValue(item[key], item, {groupId, selected}) : undefined;

                        return <td
                            key={key}
                            className={
                                `halign-${halign}`
                                + (rightAlign ? ' halign-right' : '') // @deprecated
                                + (cellEmpty ? ' cell-empty' : '')
                                + (editable ? ' editable' : '')
                                + (editing ? ' editing' : '')
                            }
                            data-column={key}
                            ref={el => {
                                if (el && editing) {
                                    this.editingCell = el;
                                }
                            }}
                        >
                            <div className="cell-inner">
                                {
                                    !cellEmpty &&
                                    !isSegmentImmutable &&
                                    <div className="cell-value">{renderedValue}</div>
                                }
                                {
                                    !cellEmpty &&
                                    isSegmentImmutable &&
                                    <div className="cell-value segment-immutable">-</div>
                                }
                                {
                                    editable &&
                                    <button
                                        type="button"
                                        className={'cell-edit-button' + (inlineEditorIsSubmitting ? ' disabled' : '')}
                                        onClick={() => {
                                            if (!inlineEditorIsSubmitting) {
                                                if (!editing) {
                                                    this.setState({
                                                        inlineEditing: {
                                                            groupId: groupId,
                                                            itemId: item.id,
                                                            column: key
                                                        }
                                                    });
                                                }
                                            }
                                        }}
                                        ref={el => {
                                            if (el && editing) {
                                                this.editingCellEditButton = el;
                                            }
                                        }}
                                    >
                                        <Icon name="pencil"/>
                                    </button>
                                }
                            </div>
                        </td>;
                    })
                }
            </tr>;
        });
    };

    getTotalItemRows = (itemIndex) => {
        const {
            columnConfigs,
            totalStats: items,
            totalSegmentData: segmentData,
            segmentNames,
            segmentNameColumn,
            segmentImmutableColumns,
            selectable,
            totalStatsNameColumn,
            totalStatsNames
        } = this.props;

        const groupId = null;

        let item = items[itemIndex];
        item.__type = 'total';

        const rowObjects = [item];
        if (segmentData && segmentData.length > 0) {
            segmentData[itemIndex].forEach((seg, segIndex) => {
                if (seg === null) {
                    return;
                }
                const obj = {__type: 'segment', id: item.id};
                let isSegmentStarted = false;
                columnConfigs.forEach(({key}) => {
                    if (key === segmentNameColumn) {
                        obj[key] = segmentNames ? segmentNames[segIndex] : seg[key];
                        isSegmentStarted = true;
                        return;
                    }

                    if (!isSegmentStarted) {
                        return;
                    }

                    if (segmentImmutableColumns.indexOf(key) > -1) {
                        obj[key] = {__type: 'segmentImmutable'};
                        return;
                    }

                    if (!seg.hasOwnProperty(key)) {
                        return;
                    }

                    obj[key] = seg[key];
                });
                rowObjects.push(obj);
            });
        }
        rowObjects.push({__type: 'divider', id: item.id});

        return rowObjects.map((item, index) => {
            return <tr key={this.getItemRowKey(item.id, index, groupId)}
                       className={
                           `row-${item.__type}`
                       }
            >
                {
                    selectable &&
                    <td className="cell-checkbox"/>
                }
                {
                    columnConfigs.map(({key, renderValue = val => val, renderEditor, halign = 'left', rightAlign}) => {
                        const isTitleCell = item.__type === 'total' && key === totalStatsNameColumn;
                        const cellValue = isTitleCell ? totalStatsNames[itemIndex] : item[key];
                        const cellEmpty = item.__type === 'divider' || cellValue === undefined;
                        const isSegmentImmutable = item[key] !== null && item[key] !== undefined && item[key].__type === 'segmentImmutable';
                        const renderedValue = !cellEmpty && !isSegmentImmutable ? renderValue(cellValue, item, {groupId}) : undefined;

                        return <td
                            key={key}
                            className={
                                `halign-${halign}`
                                + (rightAlign ? ' halign-right' : '') // @deprecated
                                + (cellEmpty ? ' cell-empty' : '')
                                + (isTitleCell ? ' cell-title' : '')
                            }
                            data-column={key}
                        >
                            <div className="cell-inner">
                                {
                                    !cellEmpty &&
                                    !isSegmentImmutable &&
                                    <div className="cell-value">{renderedValue}</div>
                                }
                                {
                                    !cellEmpty &&
                                    isSegmentImmutable &&
                                    <div className="cell-value segment-immutable">-</div>
                                }
                            </div>
                        </td>;
                    })
                }
            </tr>;
        });
    };

    getTableHeader = (ref) => {
        const {
            columnConfigs,
            items,
            updateSorting,
            selectable
        } = this.props;

        const {selectedItemIds} = this.state;

        const sorting = jsonCopy(this.props.sorting || {key: null, isAsc: true});

        return <thead>
        <tr ref={ref}>
            {
                selectable &&
                <td className="cell-checkbox">
                    <div className="checkbox"
                         onClick={this.toggleAllItemsSelected}
                    >
                        {
                            selectedItemIds.length > 0 && selectedItemIds.length === items.length
                                ? <Icon name="checkbox-checked"/>
                                : (
                                    selectedItemIds.length > 0
                                        ? <Icon name="checkbox-minus"/>
                                        : <Icon name="checkbox-blank"/>
                                )
                        }
                    </div>
                </td>
            }
            {
                columnConfigs.map(({key, name, hint, sortable, halign = 'left', sortKey, rightAlign}) => {
                    // sortKey is deprecated
                    sortKey = sortKey ? sortKey : (sortable ? key : null);

                    return <td key={key}
                        className={
                            `halign-${halign}`
                            + (rightAlign ? ' halign-right' : '') // @deprecated
                            + (sortKey ? ' sortable' : '') // @deprecated
                            + (sorting.key === sortKey ? ' sorted' : '')
                        }
                        data-column={key}
                        onClick={ev => {
                            if (sortKey) {
                                if (sorting.key !== sortKey) {
                                    sorting.key = sortKey;
                                    sorting.isAsc = false;
                                } else if (!sorting.isAsc) {
                                    sorting.isAsc = true;
                                } else {
                                    sorting.key = null;
                                    sorting.isAsc = false;
                                }
                                updateSorting(sorting);
                            }
                        }}
                    >
                        <div className="cell-inner">
                            <span className="text">{name}</span>
                            {
                                hint &&
                                <button
                                    className="hint-button"
                                    type="button"
                                    aria-label={hint}
                                    aria-controls="tooltip-click-to-hold tooltip-should-set-width-fit-text"
                                >
                                    <Icon name="question-circle"/>
                                </button>
                            }
                            {
                                sortKey &&
                                sorting.key === sortKey &&
                                <div className="sort-indicator">
                                    {
                                        sorting.isAsc
                                            ? <Icon name='arrow-up'/>
                                            : <Icon name='arrow-down'/>
                                    }
                                </div>
                            }
                        </div>
                    </td>;
                })
            }
        </tr>
        </thead>;
    };

    getTableBody = () => {
        const {
            columnConfigs,
            renderGroupHeadline,
            items,
            groups,
            totalStats = [],
            selectable
        } = this.props;

        const hasGroups = groups && groups.length > 0;

        return <tbody>
        {
            totalStats.length > 0 &&
            items.length > 0 &&
            totalStats.map((item, index) => this.getTotalItemRows(index))
        }
        {
            hasGroups
                ? groups
                    .reduce((result, group) => {
                        const totalColumns = columnConfigs.length + (selectable ? 1 : 0);
                        return result.concat([
                            <tr key={this.getGroupRowKey(group.id, 0)} className="row-group">
                                <td colSpan={totalColumns}>
                                    <div className="cell-inner">
                                        <div className="cell-value">
                                            {renderGroupHeadline(group)}
                                        </div>
                                    </div>
                                </td>
                            </tr>,
                            <tr key={this.getGroupRowKey(group.id, 1)} className="row-divider">
                                <td colSpan={totalColumns}>
                                    <div className="cell-inner"/>
                                </td>
                            </tr>
                        ]).concat(group.itemIds.map(id => {
                            return this.getSegmentedItemRows(id, group.id);
                        }));
                    }, [])
                : items
                    .reduce((result, item) => {
                        return result.concat(this.getSegmentedItemRows(item.id));
                    }, [])
        }
        {
            totalStats.length > 0 &&
            totalStats.map((item, index) => this.getTotalItemRows(index))
        }
        </tbody>
    };

    getScrollBar = () => {
        return <div
            className="scroll-bar"
            style={{
                position: 'fixed',
                bottom: '0px'
            }}
            ref={el => this.scrollBar = el}
        >
            <div
                className="track"
                style={{
                    position: 'relative'
                }}
                ref={el => this.scrollBarTrack = el}
            >
                <div
                    className="thumb"
                    style={{
                        position: 'absolute'
                    }}
                    ref={el => this.scrollBarThumb = el}
                />
            </div>
        </div>;
    };

    getEditor = (editingItem, renderEditor) => {
        return renderEditor(
            editingItem,
            // onSubmittingChange
            (isSubmitting) => {
                this.setState({
                    inlineEditorIsSubmitting: isSubmitting
                });
            },
            // close
            () => {
                this.setState({
                    inlineEditing: {
                        groupId: null,
                        itemId: null,
                        column: null
                    }
                });
            },
            // updatePosition
            () => this.updateInlineEditorPosition()
        );
    };

    render() {
        const {
            columnConfigs,
            items,
            segmentData,
            selectable,
            theme
        } = this.props;

        const {
            inlineEditing,
            shouldShowInlineEditor,
            horizontalScrollDisabledTemporarily
        } = this.state;

        const hasSegmentData = segmentData && segmentData.length > 0;

        const editingItem = items.find(item => inlineEditing.itemId === item.id);

        const editingColumnConfig = columnConfigs.find(columnConfig => inlineEditing.column === columnConfig.key);

        return <div
            className={
                "segmented-table-component"
                + (hasSegmentData ? ' segmented' : '')
                + (selectable ? ' selectable' : '')
                + (theme ? ` theme-${theme}` : '')
            }
        >
            <div
                className="sticky-header"
                style={{
                    position: 'sticky',
                    top: 0,
                    overflow: 'hidden',
                }}
                ref={el => this.stickyHeader = el}
            >
                <table>
                    {
                        this.getTableHeader(el => this.stickyHeaderRow = el)
                    }
                </table>
            </div>
            <div className="scroll-view"
                  ref={el => this.scrollView = el}
                  style={{
                      boxSizing: 'content-box',
                      width: '100%',
                      padding: '0px',
                      border: 'none',
                      overflow: 'auto'
                  }}
            >
                {
                    horizontalScrollDisabledTemporarily ||
                    <table
                        className="segmented-table"
                        ref={el => this.table = el}
                    >
                        {
                            this.getTableHeader(el => this.tableHeaderRow = el)
                        }
                        {
                            this.getTableBody()
                        }
                    </table>
                }
            </div>
            {
                this.getScrollBar()
            }
            {
                shouldShowInlineEditor &&
                editingItem &&
                editingColumnConfig &&
                <div className="inline-editor-wrapper"
                     style={{
                         position: 'absolute'
                     }}
                     ref={el => this.inlineEditor = el}
                >
                    {
                        this.getEditor(editingItem, editingColumnConfig.renderEditor)
                    }
                </div>
            }
        </div>
    }
}
